import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import {
  Card,
  CardContent,
  Grid,
  Typography,
  Box,
  Skeleton,
  Stack,
  Paper,
  Fade,
  alpha,
  useTheme,
} from '@mui/material';
import * as actions from '../../redux/actions';

import SalaryListDetailCard from '../../components/Card/SalaryListDetailCard';
import SalaryDetail from '../../components/Table/SalaryDetail';
import { payrollReport } from '../../components/PDF';
import TimestampProjectCost from '../../components/Table/TimestampProjectCost';
import { confirmAsync, showAlert } from '../../utils/functions/globalDialog';

export default function HrSalaryDetail({ title, subtitle, id: idProp, onDeleted }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const history = useHistory();
  const { id: idFromRoute } = useParams();
  const id = idProp || idFromRoute;
  // ใช้ state.salaryDetail (แยกจาก state.salaryList ที่หน้ารายการเงินเดือนใช้) กัน fetch
  // รายละเอียด 1 รายการนี้ไปทับ rows ของหน้ารายการที่อาจ mount ค้างอยู่เบื้องหลัง (เปิดเป็น modal)
  const salaryList = useSelector((state) => state.salaryDetail);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await dispatch(actions.salaryDetailGet(id));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  // ลบรายการเงินเดือน — backend คืนค่า status_payroll ของ checkin ทั้งหมดในงวดนี้กลับเป็น false
  // ให้เองอยู่แล้ว (salaryList.service.js) งวดนี้เลยกลับไปแก้ไข/บันทึกใหม่ได้ทันทีหลังลบ
  const handleDelete = async () => {
    if (!(await confirmAsync('ต้องการลบรายการเงินเดือนนี้หรือไม่? ข้อมูลจะถูกลบถาวร และงวดนี้จะกลับไปแก้ไข/บันทึกใหม่ได้อีกครั้ง'))) {
      return;
    }
    const res = await dispatch(actions.salaryListDelete(id));
    if (res?.preparedWarning?.length) {
      await showAlert(`ลบงวดเงินเดือนแล้ว แต่มีรายการเบิกค่าแรงที่เตรียมจ่ายไปแล้ว ${res.preparedWarning.length} รายการ (${res.preparedWarning.join(', ')}) ระบบไม่ได้ลบให้ กรุณาตรวจสอบเอง`);
    }
    if (onDeleted) {
      onDeleted();
    } else {
      history.push('/humen/salarylist');
    }
  };

  const renderSkeletonDetail = () => (
    <Fade in={true} timeout={300}>
      <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Skeleton variant="rounded" width={40} height={40} animation="wave" />
            <Box flex={1}>
              <Skeleton variant="text" width={200} height={32} animation="wave" />
              <Skeleton variant="text" width={150} height={24} animation="wave" />
            </Box>
          </Stack>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Skeleton variant="rounded" width="100%" height={60} animation="wave" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Skeleton variant="rounded" width="100%" height={60} animation="wave" />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Fade>
  );

  const renderSkeletonSummary = () => (
    <Fade in={true} timeout={300}>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            {[1, 2, 3, 4].map((item) => (
              <Grid item xs={12} md={3} key={item}>
                <Skeleton variant="text" width={150} height={24} animation="wave" />
                <Skeleton variant="text" width={120} height={40} animation="wave" />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Fade>
  );

  const renderSkeletonTable = () => (
    <Fade in={true} timeout={300}>
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
        <Box sx={{ p: 2 }}>
          {[1, 2, 3, 4, 5].map((row) => (
            <Box key={row} sx={{ mb: 2 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Skeleton variant="circular" width={40} height={40} animation="wave" />
                <Box flex={1}>
                  <Skeleton variant="text" width="60%" animation="wave" />
                  <Skeleton variant="text" width="40%" height={12} animation="wave" />
                </Box>
                <Skeleton variant="text" width={100} animation="wave" />
              </Stack>
            </Box>
          ))}
        </Box>
      </Card>
    </Fade>
  );

  const renderDetail = () => {
    if (loading) return renderSkeletonDetail();
    return (
      <SalaryListDetailCard
        salary={salaryList}
        payrollReport={payrollReport}
        onDelete={handleDelete}
      />
    );
  };

  const renderSummary = () => {
    if (loading) return renderSkeletonSummary();

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('th-TH', {
        style: 'currency',
        currency: 'THB',
        minimumFractionDigits: 2,
      }).format(amount || 0);
    };

    return (
      <div className="py-2">
        <Card>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle1" color="textSecondary">
                  ยอดจ่ายสุทธิ (Total Net)
                </Typography>
                <Typography variant="h5" color="primary">
                  {formatCurrency(salaryList?.total)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle1" color="textSecondary">
                  ภาษีหัก ณ ที่จ่าย (Total Tax)
                </Typography>
                <Typography variant="h5" color="error">
                  {formatCurrency(salaryList?.total_tax)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle1" color="textSecondary">
                  ประกันสังคม (Total SSO)
                </Typography>
                <Typography variant="h5" color="error">
                  {formatCurrency(salaryList?.total_sso)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle1" color="textSecondary">
                  หักมาสาย (Total Late)
                </Typography>
                <Typography variant="h5" color="error">
                  {formatCurrency(salaryList?.total_late)}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderTable = () => {
    if (loading) return renderSkeletonTable();
    return <SalaryDetail salary={salaryList?.salaryData} />;
  };

  const renderDashboard = () => {
    if (loading) return null;
    const totalSsoAmount = (salaryList?.salaryData || []).reduce(
      (sum, r) => sum + parseFloat(r?.expenses?.sso || 0),
      0,
    );
    return (
      <TimestampProjectCost
        timestamp={{ dashboard: salaryList?.dashboard }}
        totalSsoAmount={totalSsoAmount}
      />
    );
  };

  return (
    <div>
      {renderDetail()}
      {renderSummary()}
      {renderTable()}
      {renderDashboard()}
    </div>
  );
}
